/**
 * TestSandboxTab — fast-path-only simulation against POST /guardrails/test.
 * No LLM call, nothing logged (server-guaranteed — see note in the response).
 */

import React, { useState } from 'react';
import { Box, TextField, Select, MenuItem, Button, Alert, Typography, Chip, Paper, CircularProgress, List, ListItemButton, ListItemText } from '@mui/material';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import { colors } from '../../../theme/designTokens';
import { quillApi, QuillApiError, CapabilityTier, GuardrailTestResponse } from '../../../services/quillAdminService';
import { verdictColor, verdictLabel, matchTypeLabel } from './guardrailUi';

interface TestSandboxTabProps {
  tiers: CapabilityTier[];
  tiersLoading: boolean;
  tiersError: string | null;
}

interface HistoryEntry {
  query: string;
  tierId: string;
  response: GuardrailTestResponse;
  at: number;
}

const DEFAULT_TIER = 'tier_1_legacy';

const TestSandboxTab: React.FC<TestSandboxTabProps> = ({ tiers, tiersLoading, tiersError }) => {
  const [query, setQuery] = useState('');
  const [tierId, setTierId] = useState(DEFAULT_TIER);
  const [running, setRunning] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<GuardrailTestResponse | null>(null);
  const [history, setHistory] = useState<HistoryEntry[]>([]);

  const effectiveTierId = tiers.some((t) => t.tier_id === tierId) ? tierId : tiers[0]?.tier_id ?? DEFAULT_TIER;

  const run = async () => {
    if (!query.trim()) return;
    setRunning(true);
    setError(null);
    try {
      const res = await quillApi.guardrails.test({ query_text: query, tier_id: effectiveTierId });
      setResult(res);
      setHistory((prev) => [{ query, tierId: effectiveTierId, response: res, at: Date.now() }, ...prev].slice(0, 10));
    } catch (e) {
      setError(e instanceof QuillApiError ? e.message : 'Sandbox test failed');
    } finally {
      setRunning(false);
    }
  };

  const rerun = (entry: HistoryEntry) => {
    setQuery(entry.query);
    setTierId(entry.tierId);
    setResult(entry.response);
  };

  const displayVerdict = result
    ? result.match_type === 'none'
      ? 'on_mission'
      : result.verdict
    : null;

  const c = verdictColor(displayVerdict);

  return (
    <Box>
      <Alert severity="info" sx={{ mb: 2 }}>
        Fast-path simulation only — no LLM call is made and nothing is logged.
      </Alert>

      {tiersError && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          Couldn't load tiers ({tiersError}) — defaulting to {DEFAULT_TIER}.
        </Alert>
      )}

      <Paper variant="outlined" sx={{ p: 3, borderRadius: '16px', mb: 3 }}>
        <TextField
          label="Query text"
          fullWidth
          multiline
          minRows={3}
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Type a member-style query to simulate…"
          sx={{ mb: 2 }}
        />
        <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center', flexWrap: 'wrap' }}>
          <Select size="small" value={effectiveTierId} onChange={(e) => setTierId(e.target.value)} disabled={tiersLoading} sx={{ minWidth: 220 }}>
            {tiersLoading && <MenuItem value={DEFAULT_TIER}>Loading tiers…</MenuItem>}
            {!tiersLoading && tiers.length === 0 && <MenuItem value={DEFAULT_TIER}>{DEFAULT_TIER}</MenuItem>}
            {tiers.map((t) => (
              <MenuItem key={t.tier_id} value={t.tier_id}>
                {t.tier_name}
              </MenuItem>
            ))}
          </Select>
          <Button
            variant="contained"
            startIcon={running ? <CircularProgress size={16} color="inherit" /> : <PlayArrowIcon />}
            disabled={!query.trim() || running}
            onClick={run}
            sx={{ backgroundColor: colors.deepTeal[500], '&:hover': { backgroundColor: colors.deepTeal[600] } }}
          >
            Run
          </Button>
        </Box>
      </Paper>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {result && (
        <Paper variant="outlined" sx={{ p: 3, borderRadius: '16px', mb: 3, borderLeft: `4px solid ${c.border}` }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1.5 }}>
            <Chip
              label={verdictLabel(displayVerdict)}
              sx={{ backgroundColor: c.bg, color: c.text, border: `1px solid ${c.border}`, fontWeight: 700, fontSize: '0.85rem', height: 32 }}
            />
            <Chip label={matchTypeLabel(result.match_type)} size="small" variant="outlined" />
          </Box>

          <Typography variant="body2" sx={{ color: 'text.secondary', mb: 2 }}>
            {result.match_type === 'none'
              ? `${result.note} (would proceed → on_mission; fast path passed; LLM classifier would evaluate next)`
              : result.note}
          </Typography>

          {result.matched_rule && (
            <Box sx={{ mb: 2 }}>
              <Typography variant="caption" sx={{ fontWeight: 700, textTransform: 'uppercase', color: 'text.secondary', letterSpacing: '0.06em' }}>
                Matched rule
              </Typography>
              <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                {result.matched_rule.rule_key} · {result.matched_rule.category} · priority {result.matched_rule.priority}
              </Typography>
            </Box>
          )}

          {result.refusal_preview && (
            <Box>
              <Typography variant="caption" sx={{ fontWeight: 700, textTransform: 'uppercase', color: 'text.secondary', letterSpacing: '0.06em', display: 'block', mb: 0.5 }}>
                Refusal preview
              </Typography>
              <Box
                sx={{
                  backgroundColor: colors.softCream[200],
                  borderRadius: '18px',
                  borderTopLeftRadius: '4px',
                  p: 2,
                  maxWidth: 480,
                }}
              >
                <Typography variant="body2" sx={{ lineHeight: 1.6 }}>
                  {result.refusal_preview}
                </Typography>
              </Box>
            </Box>
          )}
        </Paper>
      )}

      {history.length > 0 && (
        <Box>
          <Typography sx={{ fontWeight: 600, mb: 1 }}>Recent runs</Typography>
          <List dense sx={{ bgcolor: 'background.paper', borderRadius: '12px', border: '1px solid', borderColor: 'divider' }}>
            {history.map((entry, i) => {
              const hc = verdictColor(entry.response.match_type === 'none' ? 'on_mission' : entry.response.verdict);
              return (
                <ListItemButton key={i} onClick={() => rerun(entry)}>
                  <ListItemText
                    primary={entry.query.length > 80 ? `${entry.query.slice(0, 80)}…` : entry.query}
                    secondary={new Date(entry.at).toLocaleTimeString()}
                  />
                  <Chip
                    label={verdictLabel(entry.response.match_type === 'none' ? 'on_mission' : entry.response.verdict)}
                    size="small"
                    sx={{ backgroundColor: hc.bg, color: hc.text }}
                  />
                </ListItemButton>
              );
            })}
          </List>
        </Box>
      )}
    </Box>
  );
};

export default TestSandboxTab;
